/* global theme */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.updateElementsVisibility();
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.variant = null;

      // Get selected variant data (if variant exists).
      this.variant = this.getSelectedVariant();
      const isDrawer = this.closest('quick-add-drawer') !== null;
      const sparePartsContainer = document.querySelector('.m-spare-parts');
      const fitsToContainer = document.querySelector('.js-m-fits-to');

      if (this.variant) {
        if (sparePartsContainer) {
          sparePartsContainer.style.display = 'block';
        }
        if (fitsToContainer) {
          fitsToContainer.style.display = 'block';
        }
        const messageContainer = this.section.querySelector('.variant-unavailable-message');
        if (messageContainer) {
          messageContainer.remove();
        }
        if (!isDrawer) {
          this.updateMedia();
          this.updateUrl(evt);
          this.updateVariantInput();
        }
      } else {
        this.showUnavailableOptionMessage();
        const preorderMessage = document.querySelector('preorder-message');
        const preorderLabel = document.querySelector('.js-variant-label');
        if (sparePartsContainer) {
          sparePartsContainer.style.display = 'none';
        }
        if (fitsToContainer) {
          fitsToContainer.style.display = 'none';
        }
        if (preorderMessage && preorderLabel) {
          preorderMessage.hide();
          preorderLabel.setAttribute('hidden', 'true');
        }
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updatePreorderMessage();
      this.updateQuickAddPreorderElements();
      this.updatePreorderAttributes();
      this.updateSku();
      VariantPicker.updateLabelText(evt);
      this.updateElementsVisibility();

      this.dispatchEvent(
        new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant || null,
            product: this.data.product,
            drawer: isDrawer,
          },
        }),
      );
    }

    showUnavailableOptionMessage() {
      const parentContainer = this.closest('#product-main-info');
      if (!parentContainer) return;

      const skuContainer = parentContainer.querySelector('.product-vendor-sku');
      let messageContainer = parentContainer.querySelector('.price__no-variant');

      if (messageContainer) {
        messageContainer.removeAttribute('hidden');
        messageContainer.parentElement?.removeAttribute('hidden');
      } else if (skuContainer) {
        messageContainer = document.createElement('div');
        messageContainer.classList.add('price__no-variant');
        skuContainer.appendChild(messageContainer);
      } else {
        return;
      }

      messageContainer.innerHTML = `
          <div class="variant-unavailable-message" style="color: rgb(var(--very-low-stock-text-color)); display: flex; gap: 1.6rem">
              <svg style="flex:1 0 auto; line-height: 2.4rem" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" rx="12" fill="#B40C1C"/>
                  <path d="M13.0469 5L12.8594 13.0234H11.25L11.0547 5H13.0469ZM11 16.4844C11 16.1979 11.0938 15.9583 11.2812 15.7656C11.474 15.5677 11.7396 15.4688 12.0781 15.4688C12.4115 15.4688 12.6745 15.5677 12.8672 15.7656C13.0599 15.9583 13.1562 16.1979 13.1562 16.4844C13.1562 16.7604 13.0599 16.9974 12.8672 17.1953C12.6745 17.388 12.4115 17.4844 12.0781 17.4844C11.7396 17.4844 11.474 17.388 11.2812 17.1953C11.0938 16.9974 11 16.7604 11 16.4844Z" fill="white"/>
              </svg>
              <div>
                  <p style="font-size: 2rem; margin-bottom: 0.8rem; line-height: 2.4rem">${window.theme.strings.combinationIsNotAvailable}</p>
                  <span style="display: inline-block; font-size: 1.6rem; line-height: 2.4rem">${window.theme.strings.combinationIsNotAvailableDescription}</span>
              </div>
          </div>
      `;
    }

    /**
     * Updates the elements' visibility according to price.
     */
    updateElementsVisibility() {
      if (!this.variant) return;

      const { stockBalanceStatus } = this.data.formatted[this.variant.id];
      const elementsToCheck = [
        ...this.section.querySelectorAll('.product-info__price > .price'),
        ...this.section.querySelectorAll('.product-info__add-to-cart'),
        ...this.section.querySelectorAll('.product-policies'),
        ...this.section.querySelectorAll('preorder-message'),
        ...this.section.querySelectorAll('product-inventory'),
        ...this.section.querySelectorAll('.product-info__block:has([id*="bundler"])'),
      ];

      elementsToCheck.forEach((element) => {
        element.hidden = this.variant.price === 0 || (!this.variant.available && stockBalanceStatus === 'R');
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      this.notifyMeBtn = document.querySelector('m-notify-me');

      const variantAvailable = this.variant && this.variant.available;
      const preorderStr = theme.strings.preorder;

      if (!this.notifyMeBtn.isAppInstalled) this.addBtn.disabled = !variantAvailable;
      this.notifyMeBtn.dataset.variantAvailable = variantAvailable;

      if (
        variantAvailable &&
        !(this.section.matches('quick-add-drawer') && this.productForm.querySelector('.shopify-payment-button'))
      ) {
        this.addBtn.classList.add('btn--primary');
        this.addBtn.classList.remove('btn--secondary');
      } else {
        this.addBtn.classList.remove('btn--primary');
        this.addBtn.classList.add('btn--secondary');
      }

      if (!variantAvailable) {
        if (this.section.matches('quick-add-drawer')) {
          this.addBtn.disabled = true;
        } else {
          this.addBtn.textContent = this.notifyMeBtn.dataset.buttonText;
          this.addBtn.innerHTML += `<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M4 7.33904V17.5H21V7.33903L12.9605 13.592L12.5 13.9501L12.0395 13.592L4 7.33904ZM19.6355 6.5H5.36447L12.5 12.0499L19.6355 6.5ZM2.5 6C2.5 5.44772 2.94772 5 3.5 5H21.5C22.0523 5 22.5 5.44772 22.5 6V18C22.5 18.5523 22.0523 19 21.5 19H3.5C2.94772 19 2.5 18.5523 2.5 18V6Z" fill="currentColor"/>
        </svg>`;
        }
      } else if (this.isActivePreorder()) {
        this.addBtn.textContent = preorderStr;
      } else {
        this.addBtn.disabled = false;
        this.addBtn.textContent = this.addBtn.dataset.addToCartText;
      }
    }

    /**
     * Updates preorder attributes based on variant availability and preorder state
     */
    updatePreorderAttributes() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;
      if (!this.variant) return;

      const { preorderItemStatusAttribute } = theme.strings;

      this.preorderStatusAttribute =
        this.preorderStatusAttribute ||
        this.productForm.querySelector(`[name="properties[${preorderItemStatusAttribute}]"]`);

      this.preorderAttribute =
        this.preorderAttribute || this.productForm.querySelector('[name="properties[_preorder]"]');

      const isVariantAvailable = this.variant.available;
      const isDisabled = isVariantAvailable && !this.isActivePreorder();

      if (this.preorderAttribute) {
        this.preorderAttribute.disabled = isDisabled;
      }

      if (this.preorderStatusAttribute) {
        this.preorderStatusAttribute.disabled = isDisabled;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;
      const selectedOptions = this.getSelectedOptions();

      if (!this.variant) {
        currVariant = { options: selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === selectedOptions[i] || selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(`[data-media-id="${this.variant.featured_media.id}"]`);
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the preorder availability.
     */
    updatePreorderMessage() {
      if (!this.variant) return;
      this.preorderMessage = this.preorderMessage || this.section.querySelector('preorder-message');
      if (!this.preorderMessage) return;

      const { preorderMessage } = this.data.formatted[this.variant.id];
      if (preorderMessage) {
        this.preorderMessage.querySelector('.rte').innerHTML = preorderMessage;
      }

      if (this.isActivePreorder()) {
        this.preorderMessage.show();
      } else if (!this.preorderMessage.classList.contains('product-message--out')) {
        this.preorderMessage.hide();
      }
    }

    /**
     * Updates the quick add preorder availability.
     */
    updateQuickAddPreorderElements() {
      if (!this.variant) return;

      this.preorderQuickAddElements =
        this.preorderQuickAddElements || this.section.querySelector('.quick-add__preorder-message');
      if (!this.preorderQuickAddElements) return;

      if (this.isActivePreorder()) {
        this.preorderQuickAddElements.classList.remove('tw-hidden');
      } else if (!this.preorderQuickAddElements.classList.contains('tw-hidden')) {
        this.preorderQuickAddElements.classList.add('tw-hidden');
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        if (priceWasEl) priceWasEl.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle('price--on-sale', this.variant.compare_at_price > this.variant.price);
        this.price.classList.toggle('price--sold-out', !this.variant.available);
      }

      this.price.querySelector('.price__default').hidden = !this.variant;
      this.price.querySelector('.price__no-variant').hidden = this.variant;
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          selectedOptions.push(selector.querySelector('.custom-select__btn').textContent.trim());
        } else {
          selectedOptions.push(selector.querySelector('input:checked').value);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find((v) => v.options.every((val, index) => val === selectedOptions[index]));
    }

    /**
     * Determines whether the preorder is active.
     * @returns {boolean} - Returns true if the preorder message should be shown, otherwise false.
     */
    isActivePreorder() {
      if (!this.variant || !this.data.formatted[this.variant.id]) return false;

      const { preorderStatus } = this.data.formatted[this.variant.id];

      if (this.variant.available && preorderStatus) {
        return preorderStatus;
      }

      return false;
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
